import React, { useRef, useEffect } from 'react'
import { errorLogger, ErrorLogEntry } from '../../utils/errorLogger'
import { backendService } from '../../services/backendService'
import { useAuth } from '../../contexts/AuthContext'
import { useTabContext } from '../../contexts/TabContext'
import { useCIWState } from '../../hooks/useCIWState'
import { useStabilityManager } from '../../hooks/useStabilityManager'
import { windowCommandBus } from '../../utils/windowCommandBus'
import './CIW.css'

export const CIWWindow: React.FC = () => {
  const { doctorName, isAuthenticated, logout } = useAuth()
  const tabContext = useTabContext()

  // CIW state management with persistence
  const ciwState = useCIWState()

  // Stability manager for preventing race conditions
  const stability = useStabilityManager('CIW', {
    debounceMs: 200,
    clickCooldown: 300,
    maxConcurrentRequests: 2
  })

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-scroll to bottom when new messages are added.
  // Guard: skip when the CIW tab is hidden (offsetParent === null means the
  // element or one of its ancestors has `display: none`). Calling
  // scrollIntoView on a 0×0 hidden element causes Chromium to fall back to
  // scrolling whatever other `overflow:auto` ancestor is closest on the page,
  // which previously bled into the GirasolesView left-panel and pushed the
  // G-Object Manager off-screen.
  useEffect(() => {
    if (!ciwState.state.preferences.autoScroll) return
    const el = messagesEndRef.current
    if (!el || el.offsetParent === null) return
    el.scrollIntoView({ behavior: 'smooth' })
  }, [ciwState.state.messages, ciwState.state.preferences.autoScroll])

  // Focus input on mount and setup error logging
  useEffect(() => {
    inputRef.current?.focus()

    // Subscribe to error logger
    const unsubscribe = errorLogger.addListener((entry: ErrorLogEntry) => {
      ciwState.addMessage(entry.message, entry.type)
    })

    // Subscribe to backend messages
    const unsubscribeBackend = backendService.addMessageListener((message) => {
      ciwState.addMessage(`[Backend] ${message.message}`, message.type as any)
    })

    // Subscribe to connection status
    const unsubscribeConnection = backendService.addConnectionListener((connected, message) => {
      if (connected) {
        ciwState.addMessage('Backend connection established', 'info')
        if (isAuthenticated && doctorName) {
          ciwState.addMessage(`Authenticated as: ${doctorName}`, 'info')
        }
      } else {
        ciwState.addMessage(`Backend connection lost: ${message || 'Unknown reason'}`, 'error')
      }
    })

    // Log CIW initialization
    errorLogger.logInfo('CIW (Command Interface Window) initialized', 'CIW')

    if (isAuthenticated && doctorName) {
      ciwState.addMessage(`Welcome, ${doctorName}`, 'info')
      ciwState.addMessage('Ready for commands. Type "help" for available commands.', 'info')
    }

    return () => {
      unsubscribe()
      unsubscribeBackend()
      unsubscribeConnection()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, doctorName]) // Removed ciwState from dependencies to prevent re-initialization loop

  const handleCommandSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!ciwState.currentCommand.trim()) return

    const command = ciwState.currentCommand.trim()

    // Use stability manager for the actual command processing
    stability.safeClick('submit-command', () => {
      // Submit command (adds to console and history)
      ciwState.submitCommand(command)

      // Process command
      processCommand(command)
    }, { cooldown: 200 })()
  }

  const processCommand = async (command: string) => {
    const cmd = command.toLowerCase().trim()
    const parts = cmd.split(/\s+/)

    // ---------- Original built-in commands ----------

    if (cmd === 'help') {
      ciwState.addMessage('Available commands:', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  General:', 'info')
      ciwState.addMessage('    help                       - Show this help message', 'info')
      ciwState.addMessage('    clear                      - Clear console', 'info')
      ciwState.addMessage('    status                     - Show system status', 'info')
      ciwState.addMessage('    version                    - Show version information', 'info')
      ciwState.addMessage('    export                     - Export console log', 'info')
      ciwState.addMessage('    session                    - Show session information', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  Window management:', 'info')
      ciwState.addMessage('    list windows               - List all open windows', 'info')
      ciwState.addMessage('    focus <tabId>              - Switch to a window', 'info')
      ciwState.addMessage('    mount girasoles_view       - Open new Girasoles View', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  Girasoles View commands (gv):', 'info')
      ciwState.addMessage('    gv list-points             - List boundary points', 'info')
      ciwState.addMessage('    gv clear-points            - Clear all boundary points', 'info')
      ciwState.addMessage('    gv save-points [name]      - Save points (default: "default")', 'info')
      ciwState.addMessage('    gv load-points [name]      - Load saved points', 'info')
      ciwState.addMessage('    gv list-saves              - List saved point sets', 'info')
      ciwState.addMessage('    gv delete-save [name]      - Delete a saved point set', 'info')
      ciwState.addMessage('    gv state                   - Show view state', 'info')
      ciwState.addMessage('    gv generate-insole         - Open insole generation dialog', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  Template management (gv):', 'info')
      ciwState.addMessage('    gv upload-template         - Open template upload dialog', 'info')
      ciwState.addMessage('    gv list-templates          - List available templates', 'info')
      ciwState.addMessage('    gv delete-template <id>    - Delete a template by ID', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  STL Cache:', 'info')
      ciwState.addMessage('    cache stats                - Show cache statistics', 'info')
      ciwState.addMessage('    cache clear                - Clear all cached STL files', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  Calibration:', 'info')
      ciwState.addMessage('    calibrate <projectId> <heel_x> <heel_y> <heel_z> <mt1_x> <mt1_y> <mt1_z> <mt5_x> <mt5_y> <mt5_z>', 'info')
      ciwState.addMessage('                             - Set 3-point calibration for a project (normalised viewer coords)', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  Quick setup:', 'info')
      ciwState.addMessage('    load-stl <projectId>   - Upload a test foot STL to the project', 'info')
      ciwState.addMessage('', 'info')
      ciwState.addMessage('  V2 QA log (admin):', 'info')
      ciwState.addMessage('    gv v2 qa list [limit]      - List recent V2 QA runs', 'info')
      ciwState.addMessage('    gv v2 qa show <id>         - Show full V2 QA run detail', 'info')
      return
    }

    if (cmd === 'clear') {
      ciwState.clearMessages()
      return
    }

    if (cmd === 'mount girasoles_view') {
      ciwState.addMessage('Opening new Girasoles View...', 'info')
      tabContext.addGirasolesView()
      return
    }

    if (cmd === 'status') {
      ciwState.addMessage('System Status:', 'info')
      ciwState.addMessage('  Frontend: React 18.2.0 - Running', 'info')
      ciwState.addMessage('  Backend: Python API - Connected', 'info')
      ciwState.addMessage('  3D Engine: Three.js - Ready', 'info')
      ciwState.addMessage(`  Stability: ${stability.isStable ? 'Stable' : 'Processing'}`, 'info')
      const windows = windowCommandBus.listWindows()
      ciwState.addMessage(`  Registered windows: ${windows.length}`, 'info')
      return
    }

    if (cmd === 'version') {
      ciwState.addMessage('Girasoles CAD Workbench v3.0.0', 'info')
      ciwState.addMessage('React Frontend with Python Backend', 'info')
      return
    }

    if (cmd === 'export') {
      const exportData = ciwState.exportSession()
      const blob = new Blob([exportData], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `girasoles-console-${new Date().toISOString().split('T')[0]}.txt`
      a.click()
      URL.revokeObjectURL(url)
      ciwState.addMessage('Console log exported', 'info')
      return
    }

    if (cmd === 'session') {
      const sessionInfo = ciwState.getSessionInfo()
      ciwState.addMessage('Session Information:', 'info')
      ciwState.addMessage(`  Duration: ${Math.floor(sessionInfo.sessionDuration / 60)}m ${sessionInfo.sessionDuration % 60}s`, 'info')
      ciwState.addMessage(`  Messages: ${sessionInfo.messageCount}`, 'info')
      ciwState.addMessage(`  Commands: ${sessionInfo.commandCount}`, 'info')
      ciwState.addMessage(`  Status: ${sessionInfo.isActive ? 'Active' : 'Idle'}`, 'info')
      return
    }

    // ---------- Window management commands ----------

    if (cmd === 'list windows') {
      const tabs = tabContext.listTabs()
      const busWindows = windowCommandBus.listWindows()
      ciwState.addMessage(`Open tabs (${tabs.length}):`, 'info')
      for (const t of tabs) {
        const registered = busWindows.some(w => w.tabId === t.id)
        ciwState.addMessage(
          `  [${t.type}] ${t.title} (id: ${t.id})${registered ? ' ✓ commands' : ''}`,
          'info'
        )
      }
      return
    }

    if (parts[0] === 'focus' && parts.length >= 2) {
      const targetId = parts.slice(1).join(' ')
      const tabs = tabContext.listTabs()
      const match = tabs.find(
        t => t.id === targetId || t.title.toLowerCase() === targetId
      )
      if (match) {
        tabContext.switchTab(match.id)
        ciwState.addMessage(`Focused: ${match.title}`, 'info')
      } else {
        ciwState.addMessage(`Window not found: ${targetId}`, 'error')
      }
      return
    }

    // ---------- Cache commands ----------

    if (cmd === 'cache stats') {
      const { getSTLCacheStats } = await import('../../services/stlCache')
      const stats = await getSTLCacheStats()
      if (stats.entryCount === 0) {
        ciwState.addMessage('STL cache is empty.', 'info')
      } else {
        ciwState.addMessage(`STL cache: ${stats.entryCount} file(s), ${stats.totalSizeMB} MB total`, 'info')
        for (const e of stats.entries) {
          ciwState.addMessage(`  ${e.key} — ${e.filename} (${e.sizeMB} MB, cached ${e.cachedAt})`, 'info')
        }
      }
      return
    }

    if (cmd === 'cache clear') {
      const { clearSTLCache } = await import('../../services/stlCache')
      await clearSTLCache()
      ciwState.addMessage('STL cache cleared.', 'info')
      return
    }

    // ---------- Calibration commands ----------

    if (parts[0] === 'calibrate') {
      // calibrate <projectId> <heel_x> <heel_y> <heel_z> <mt1_x> <mt1_y> <mt1_z> <mt5_x> <mt5_y> <mt5_z>
      const projectId = parts[1]
      const coords = parts.slice(2).map(Number)

      if (!projectId || coords.length !== 9 || coords.some(isNaN)) {
        ciwState.addMessage('Usage: calibrate <projectId> <heel_x> <heel_y> <heel_z> <mt1_x> <mt1_y> <mt1_z> <mt5_x> <mt5_y> <mt5_z>', 'error')
        ciwState.addMessage('Example: calibrate da0e507d-58ec-4351-bc82-b279f157bd86 -2.87 -2.72 0.65 1.80 0.82 0.62 -0.18 1.94 0.62', 'info')
        return
      }

      ciwState.addMessage(`Resolving file ID for project ${projectId}...`, 'info')
      try {
        const stlStatus = await backendService.getStlStatus(projectId, 'stl_in')
        const fileId = stlStatus.latest?.fileId

        if (!fileId) {
          ciwState.addMessage(`No STL file found for project ${projectId}. Save an STL first.`, 'error')
          return
        }

        ciwState.addMessage(`Saving calibration to file ${fileId}...`, 'info')

        const [heelX, heelY, heelZ, mt1X, mt1Y, mt1Z, mt5X, mt5Y, mt5Z] = coords
        const result = await backendService.saveCalibration(fileId, {
          heel: { x: heelX, y: heelY, z: heelZ },
          mt1:  { x: mt1X,  y: mt1Y,  z: mt1Z },
          mt5:  { x: mt5X,  y: mt5Y,  z: mt5Z },
        })

        if (result.success) {
          ciwState.addMessage(`✅ Calibration saved: Heel=(${heelX.toFixed(2)},${heelY.toFixed(2)},${heelZ.toFixed(2)}) MT1=(${mt1X.toFixed(2)},${mt1Y.toFixed(2)},${mt1Z.toFixed(2)}) MT5=(${mt5X.toFixed(2)},${mt5Y.toFixed(2)},${mt5Z.toFixed(2)})`, 'info')
        } else {
          ciwState.addMessage(`Calibration failed: ${result.message}`, 'error')
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error'
        ciwState.addMessage(`Calibration error: ${msg}`, 'error')
      }
      return
    }

    // ---------- Quick setup: load-stl ----------

    if (parts[0] === 'load-stl') {
      const projectId = parts[1]

      if (!projectId) {
        ciwState.addMessage('Usage: load-stl <projectId>', 'error')
        ciwState.addMessage('Uploads a test foot STL to the project. Works reliably on any backend.', 'info')
        ciwState.addMessage('Example: load-stl 094dbe7c-1c4d-4f2c-bdb2-e1fb6534c786', 'info')
        return
      }

      ciwState.addMessage(`Generating foot-shaped test STL for project ${projectId}...`, 'info')

      try {
        // Build a foot-shaped polygon triangulated into a flat STL
        const pts: [number,number][] = [
          [0,0], [-15,5], [-28,20], [-34,40], [-36,60], [-34,85], [-28,105],
          [-22,130], [-18,150], [-16,165], [-12,180], [-6,195], [0,205],
          [6,195], [12,180], [16,165], [18,150], [22,130], [28,105],
          [34,85], [36,60], [34,40], [28,20], [15,5],
        ]

        let stl = 'solid test_foot\n'
        for (let i = 1; i < pts.length; i++) {
          const j = (i + 1) % pts.length
          if (j === 0) continue
          const [ax,ay] = pts[0], [bx,by] = pts[i], [cx,cy] = pts[j]
          stl += `facet normal 0 0 1\nouter loop\n  vertex ${ax} ${ay} 0\n  vertex ${bx} ${by} 0\n  vertex ${cx} ${cy} 0\nendloop\nendfacet\n`
          stl += `facet normal 0 0 -1\nouter loop\n  vertex ${ax} ${ay} -0.5\n  vertex ${cx} ${cy} -0.5\n  vertex ${bx} ${by} -0.5\nendloop\nendfacet\n`
        }
        stl += 'endsolid test_foot\n'

        const file = new File([new Blob([stl], { type: 'application/sla' })], 'test_foot.stl', { type: 'application/sla' })
        ciwState.addMessage(`Uploading ${(stl.length / 1024).toFixed(0)} KB...`, 'info')

        const result = await backendService.uploadSTLFile(file, projectId)
        if (result?.fileId) {
          ciwState.addMessage(`✅ STL uploaded — fileId: ${result.fileId}`, 'info')
          ciwState.addMessage('Now open the STL_IN view for this project.', 'info')
        } else {
          ciwState.addMessage('Upload failed: no fileId returned', 'error')
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error'
        ciwState.addMessage(`load-stl error: ${msg}`, 'error')
      }
      return
    }

    // ---------- Girasoles View commands (gv ...) ----------

    if (parts[0] === 'gv') {
      const subCmd = parts[1]
      const arg = parts.slice(2).join(' ') || undefined

      if (!subCmd) {
        ciwState.addMessage('Usage: gv <command> [args]. Type "help" for details.', 'error')
        return
      }

      // V2 QA admin commands: `gv v2 qa list [limit]` / `gv v2 qa show <id>`.
      // These hit the backend admin REST endpoints directly and do not depend
      // on a focused GirasolesView, so handle them before the bus dispatch.
      if (subCmd === 'v2' && parts[2] === 'qa') {
        const op = parts[3]
        if (op === 'list') {
          const limit = parts[4] ? Math.max(1, Math.min(100, Number(parts[4]) || 20)) : 20
          try {
            const resp = await backendService.listV2QARuns({ limit })
            const runs = resp.runs ?? []
            if (runs.length === 0) {
              ciwState.addMessage('No V2 QA runs.', 'info')
            } else {
              ciwState.addMessage(`V2 QA runs (${runs.length}):`, 'info')
              for (const r of runs) {
                const m = (r.metrics ?? {}) as Record<string, unknown>
                const vol = typeof m.volume_mm3 === 'number' ? `${(m.volume_mm3 as number).toFixed(0)} mm³` : '?'
                ciwState.addMessage(
                  `  ${r.id} | ${r.created_at} | ${r.verdict} | vol=${vol}`,
                  'info'
                )
              }
            }
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e)
            ciwState.addMessage(`V2 QA list failed: ${msg}`, 'error')
          }
          return
        }
        if (op === 'show' && parts[4]) {
          const runId = parts[4]
          try {
            const detail = await backendService.getV2QARun(runId)
            ciwState.addMessage(`V2 QA run ${detail.id}:`, 'info')
            ciwState.addMessage(`  created_at: ${detail.created_at}`, 'info')
            ciwState.addMessage(`  verdict: ${detail.verdict}`, 'info')
            if (detail.refine_report) {
              const rr = detail.refine_report as Record<string, unknown>
              ciwState.addMessage(
                `  refine: total=${rr.total_seconds}s peak_rss=${rr.peak_rss_mb}MB watertight=${rr.watertight}`,
                'info'
              )
            }
            if (detail.metrics) {
              ciwState.addMessage(`  metrics: ${JSON.stringify(detail.metrics)}`, 'info')
            }
            if (detail.stl_quality) {
              ciwState.addMessage(`  stl_quality: ${JSON.stringify(detail.stl_quality)}`, 'info')
            }
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e)
            ciwState.addMessage(`V2 QA show failed: ${msg}`, 'error')
          }
          return
        }
        ciwState.addMessage('Usage: gv v2 qa list [limit] | gv v2 qa show <id>', 'error')
        return
      }

      let busCommand: string
      const busArgs: Record<string, unknown> = {}

      switch (subCmd) {
        case 'list-points':
          busCommand = 'list-points'
          break
        case 'clear-points':
          busCommand = 'clear-points'
          break
        case 'save-points':
          busCommand = 'save-points'
          if (arg) busArgs.name = arg
          break
        case 'load-points':
          busCommand = 'load-points'
          if (arg) busArgs.name = arg
          break
        case 'list-saves':
          busCommand = 'list-saves'
          break
        case 'delete-save':
          busCommand = 'delete-save'
          if (arg) busArgs.name = arg
          break
        case 'state':
          busCommand = 'get-state'
          break
        case 'generate-insole':
          busCommand = 'generate-insole'
          break
        case 'upload-template':
          busCommand = 'upload-template'
          break
        case 'list-templates':
          busCommand = 'list-templates'
          break
        case 'delete-template':
          busCommand = 'delete-template'
          if (arg) busArgs.id = arg
          break
        default:
          ciwState.addMessage(`Unknown gv command: ${subCmd}`, 'error')
          return
      }

      const result = await windowCommandBus.dispatch({
        target: 'girasoles_view',
        command: busCommand,
        args: Object.keys(busArgs).length > 0 ? busArgs : undefined,
      })

      if (!result.success) {
        ciwState.addMessage(`Error: ${result.error}`, 'error')
        return
      }

      // Format output based on command
      if (busCommand === 'list-points') {
        const points = result.data as Array<{ index: number; x: number; y: number; z: number }>
        if (points.length === 0) {
          ciwState.addMessage('No boundary points selected.', 'info')
        } else {
          ciwState.addMessage(`Boundary points (${points.length}):`, 'info')
          for (const p of points) {
            ciwState.addMessage(
              `  [${p.index}] x=${p.x.toFixed(4)} y=${p.y.toFixed(4)} z=${p.z.toFixed(4)}`,
              'info'
            )
          }
        }
      } else if (busCommand === 'list-saves') {
        const sets = result.data as Array<{ name: string; points: number; updated: string }>
        if (sets.length === 0) {
          ciwState.addMessage('No saved point sets.', 'info')
        } else {
          ciwState.addMessage(`Saved point sets (${sets.length}):`, 'info')
          for (const s of sets) {
            ciwState.addMessage(`  "${s.name}" — ${s.points} points (${s.updated})`, 'info')
          }
        }
      } else if (busCommand === 'get-state') {
        const state = result.data as Record<string, unknown>
        ciwState.addMessage('Girasoles View state:', 'info')
        for (const [k, v] of Object.entries(state)) {
          ciwState.addMessage(`  ${k}: ${v}`, 'info')
        }
      } else if (busCommand === 'list-templates') {
        const templates = result.data as Array<{ id: string; name: string; size?: string; type?: string; is_shared?: boolean; created_by?: string }>
        if (templates.length === 0) {
          ciwState.addMessage('No templates available.', 'info')
        } else {
          ciwState.addMessage(`Templates (${templates.length}):`, 'info')
          for (const t of templates) {
            const vis = t.is_shared ? '🌐' : '🔒'
            ciwState.addMessage(
              `  ${vis} ${t.name} (${t.type || '?'}, size: ${t.size || '?'}) — id: ${t.id}`,
              'info'
            )
          }
        }
      } else {
        ciwState.addMessage(String(result.data), 'info')
      }

      return
    }

    // ---------- Unknown command ----------
    ciwState.addMessage(`Unknown command: ${command}`, 'error')
    ciwState.addMessage('Type "help" for available commands', 'info')
  }

  const handleMenuAction = (action: string) => {
    stability.safeClick(`menu-action-${action}`, () => {
      switch (action) {
        case 'clear-console':
          ciwState.clearMessages()
          break
        case 'export-log':
          processCommand('export')
          break
        case 'preferences':
          ciwState.addMessage('Preferences panel not yet implemented', 'warning')
          break
        case 'about':
          ciwState.addMessage('Girasoles CAD Workbench v3.0.0', 'info')
          ciwState.addMessage('Professional CAD application for orthopedic insole design', 'info')
          ciwState.addMessage('© 2024 Gira-Soles.com', 'info')
          break
        default:
          ciwState.addMessage(`Menu action "${action}" not implemented`, 'warning')
      }
    }, { cooldown: 300 })()
  }

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  return (
    <div className="ciw-window">
      {/* Menu Bar */}
      <div className="ciw-menu-bar">
        <div className="menu-items">
          <div className="menu-dropdown">
            <span className="menu-item">File</span>
            <div className="dropdown-content">
              <div className="dropdown-item" onClick={() => handleMenuAction('clear-console')}>
                Clear Console
              </div>
              <div className="dropdown-item" onClick={() => handleMenuAction('export-log')}>
                Export Log
              </div>
            </div>
          </div>
          <div className="menu-dropdown">
            <span className="menu-item">Edit</span>
            <div className="dropdown-content">
              <div className="dropdown-item" onClick={() => handleMenuAction('preferences')}>
                Preferences
              </div>
            </div>
          </div>
          <div className="menu-dropdown">
            <span className="menu-item">View</span>
            <div className="dropdown-content">
              <div className="dropdown-item" onClick={() => processCommand('status')}>
                System Status
              </div>
            </div>
          </div>
          <div className="menu-dropdown">
            <span className="menu-item">Tools</span>
            <div className="dropdown-content">
              <div className="dropdown-item" onClick={() => processCommand('help')}>
                Show Commands
              </div>
            </div>
          </div>
          <div className="menu-dropdown">
            <span className="menu-item">Help</span>
            <div className="dropdown-content">
              <div className="dropdown-item" onClick={() => handleMenuAction('about')}>
                About
              </div>
            </div>
          </div>
        </div>
        <div className="ciw-actions">
          <button className="logout-btn" onClick={() => { if (confirm('Are you sure you want to logout?')) { logout() } }} aria-label="Logout">Logout</button>
        </div>
      </div>

      {/* Console Log Area */}
      <div className="console-log" data-testid="ciw-console">
        {ciwState.state.messages.map(msg => (
          <div key={msg.id} className={`console-message ${msg.type}`}>
            {ciwState.state.preferences.showTimestamps && (
              <span className="timestamp">[{formatTimestamp(msg.timestamp)}]</span>
            )}
            <span className="message">{msg.message}</span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Command Input */}
      <form className="command-input-form" onSubmit={handleCommandSubmit} data-testid="ciw-input-form">
        <span className="command-prompt" data-testid="ciw-prompt">Command: &gt;</span>
        <input
          ref={inputRef}
          type="text"
          value={ciwState.currentCommand}
          onChange={(e) => ciwState.setCurrentCommand(e.target.value)}
          className="command-input"
          placeholder="Enter command..."
          autoComplete="off"
          data-testid="ciw-input"
        />
      </form>
    </div>
  )
}
